import { ReactNode, useEffect, useState } from "react";
import {
  ChangeStoreModal,
  InfiniteLoader,
  LeftSidebar,
  OrderOptionModal,
  Sidebar,
} from "../components";
import TopBar from "./_components/TopBar";
import RightSidebar from "./_components/RightSidebar";
import { MobileFooter } from "./_components/MobileFooter";
import { useLayout, useLoyalty } from "../context";
import { useAuth, useCart, useStore } from "../hooks";
import { Link, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import AuthModals from "../components/auth/AuthModals";
import { googleLogout } from "@react-oauth/google";
import { Toast } from "../components/Toast";

type Props = {
  className?: string;
  pageTitle?: string;
  leftSidebar?: ReactNode;
  children?: ReactNode;
  rightSidebar?: ReactNode;
  hideRightSidebarOnMobile?: boolean;
  header?: boolean;
  footer?: boolean;
  showCartIcon?: boolean;
  showLoader?: boolean;
};

const Layout: React.FC<Props> = ({
  className = "",
  children,
  footer = true,
  showCartIcon = true,
  rightSidebar,
  hideRightSidebarOnMobile = true,
  showLoader = false,
}) => {
  const [showChangeStore, setShowChangeStore] = useState<boolean>(false);

  let navigate = useNavigate();
  const { pathname } = useLocation();
  const isMenuPage = pathname === "/menu" || pathname.startsWith("/menu/");
  const isCheckoutPage =
    pathname === "/checkout" || pathname.startsWith("/checkout/");

  const { isMenuLoading, storeLocations } = useStore();
  const { setCartId, setCartItems } = useCart();
  const { setLoyalty } = useLoyalty();
  const { logout } = useAuth();

  const {
    showMenu,
    toggleMenu,
    setShowMenu,
    isSidebarCollapsed,
    showCart,
    setShowCart,
    toggleCart,
    showOptionModal,
    setShowOptionModal,
    setIsSidebarCollapsed,
    activeModal,
    setActiveModal,
  } = useLayout();

  useEffect(() => {
    setShowMenu(false);
    setShowCart(false);
    setShowOptionModal(false);
    setActiveModal(null);
    setShowChangeStore(false);
  }, [
    pathname,
    setShowMenu,
    setShowCart,
    setShowOptionModal,
    setActiveModal,
    setShowChangeStore,
  ]);

  useEffect(() => {
    const body = document.body;
    const hasOpenOverlay =
      showMenu ||
      showCart ||
      showOptionModal ||
      showChangeStore ||
      activeModal !== null;

    body.classList.toggle("modal-open", hasOpenOverlay);
    body.style.overflow = hasOpenOverlay ? "hidden" : "";

    return () => {
      body.classList.remove("modal-open");
      body.style.overflow = "";
    };
  }, [showMenu, showCart, showOptionModal, showChangeStore, activeModal]);

  useEffect(() => {
    if (isMenuPage) {
      setIsSidebarCollapsed(true);
    } else {
      setIsSidebarCollapsed(false);
    }
  }, [isMenuPage, setIsSidebarCollapsed]);

  const handleChangeStore = () => setShowChangeStore(true);

  const handleLogout = () => {
    setLoyalty(undefined);
    logout();
    googleLogout();
    setCartId("");
    setCartItems([]);
    Toast.success("You have been logged out successfully");
    // Use setTimeout to ensure state updates are processed before navigation
    setTimeout(() => {
      navigate("/");
    }, 100);
  };

  return (
    <>
      <div className={`home_with_login h-100 ${className}`}>
        <div
          className="d-flex flex-wrap flex-md-nowrap h-auto"
          style={{ minHeight: "100%" }}
        >
          <aside
            className={`left-sidebar d-none d-md-block d-lg-block ${isSidebarCollapsed ? "collapsed" : "expanded"}`}
          >
            <div className="d-flex justify-content-start align-items-center">
              <Link to={"/"} className="sidebar-logo">
                <img
                  className="m-auto"
                  src={storeLocations?.store_logo}
                  alt=""
                />
              </Link>
              {/* <button
              className="toggle-btn menu-toggle-btn d-flex justify-content-start align-items-center"
              onClick={toggleSidebar}
            >
              <CgMenuLeft />
            </button> */}
            </div>
            <Sidebar
              onChangeStore={handleChangeStore}
              onLogout={handleLogout}
              isCollapsed={isSidebarCollapsed}
            />
          </aside>
          <div
            className={clsx(
              "page-content",
              "flex-grow-1",
              isSidebarCollapsed
                ? "page-content-collapsed sidebar-collapsed"
                : "expanded sidebar-expanded",
            )}
          >
            {isMenuLoading || showLoader ? (
              <InfiniteLoader variant="success" />
            ) : (
              <></>
            )}
            {/* {header && !isMenuPage && ( */}
            <TopBar
              // pageTitle={pageTitle}
              showCartIcon={showCartIcon}
              // handleShowCart={toggleCart}
              // handleMenuToggle={toggleMenu}
            />
            {/* )} */}
            <div
              className={`${isMenuPage || isCheckoutPage ? "p-0" : "p-3 p-lg-4 page-canvas-width"}`}
            >
              {children}
            </div>
          </div>
          {rightSidebar && (
            <aside
              className={clsx(
                "right-sidebar",
                "vh-100",
                hideRightSidebarOnMobile && "d-none d-md-block",
              )}
            >
              {rightSidebar}
            </aside>
          )}
        </div>

        {footer && <MobileFooter handleChange={toggleCart} />}
      </div>

      {/* {showCartIcon && <CartButton handleClick={toggleCart} />} */}

      <LeftSidebar
        show={showMenu}
        handleChangeStore={handleChangeStore}
        onLogout={handleLogout}
        handleClose={toggleMenu}
      />
      <RightSidebar show={showCart} handleClose={toggleCart} />

      <OrderOptionModal
        show={showOptionModal}
        handleClose={() => setShowOptionModal(false)}
      />
      <ChangeStoreModal
        show={showChangeStore}
        handleClose={() => setShowChangeStore(false)}
      />

      <AuthModals
        activeModal={activeModal}
        onChange={(option) => setActiveModal(option)}
        onClose={() => setActiveModal(null)}
        onSuccess={() => setActiveModal(null)}
      />
    </>
  );
};

export default Layout;
